import UserDetail from "./UserDetail";
import { AddRounded as AddIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Typography,
} from "@mui/material";
import { adjustDate, readableDate } from "common/dates";
import Component from "components/Component";
import AdminDashboard from "components/admin/AdminDashboard";
import BigLoader from "components/common/BigLoader";
import Button from "components/common/Button";
import FormattedDatePicker from "components/common/FormattedDatePicker";
import IconButton from "components/common/IconButton";
import InlineSearch from "components/common/InlineSearch";
import DashboardContent from "components/layout/DashboardContent";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useListAuditLogsQuery } from "services/adminService";
import { useCreateAuditLogMutation } from "services/adminService";

const ManualLogDialog = ({ open, onClose, companyId, onSuccess }) => {
  const [message, setMessage] = useState("");
  const [error, setError] = useState(null);
  const [
    createAuditLog,
    { isLoading: isCreating },
  ] = useCreateAuditLogMutation();

  const handleClose = () => {
    setMessage("");
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    if (!message.trim()) {
      return;
    }

    setError(null);

    try {
      await createAuditLog({
        companyId: companyId || "ROOT",
        userMessage: message,
      }).unwrap();

      handleClose();
      onSuccess();
    } catch (err) {
      setError(
        err?.data?.message || err?.message || "Failed to create audit log"
      );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={"sm"} fullWidth>
      <DialogTitle>{"Manual Log"}</DialogTitle>
      <DialogContent>
        <TextField
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={isCreating}
          fullWidth
          multiline
          rows={4}
          sx={{ background: "white" }}
        />
        {error && (
          <Alert severity={"error"} sx={{ marginTop: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSave} disabled={isCreating || !message.trim()}>
          {"Save"}
        </Button>
        <Button onClick={handleClose} disabled={isCreating} color={"secondary"}>
          {"Cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EmptyRow = ({ children }) => (
  <TableRow>
    <TableCell colSpan={4}>{children}</TableCell>
  </TableRow>
);

const TableContent = ({ status, auditLogs }) => {
  if (status === "failed") {
    return (
      <EmptyRow>
        <Alert severity={"error"}>{"Error reading logs"}</Alert>
      </EmptyRow>
    );
  }

  if (status === "fulfilled") {
    if (!auditLogs.logs || auditLogs.logs.length === 0) {
      return (
        <EmptyRow>
          <Typography textAlign={"center"} padding={6}>
            {
              "No logs match the selected filters, or a company needs to be selected"
            }
          </Typography>
        </EmptyRow>
      );
    }

    const compareDates = (a, b) => {
      const d1 = new Date(a.date);
      const d2 = new Date(b.date);
      return d2.getTime() - d1.getTime();
    };

    const reverseSortedLogs = [...(auditLogs?.logs ?? [])].sort(compareDates);

    return reverseSortedLogs.map((log, index) => {
      const defaultMessage =
        log.userMessage === "No message supplied, allowed in dev";
      return (
        <TableRow key={index}>
          <TableCell>{readableDate(log.date, { includeTime: true })}</TableCell>
          <TableCell>
            <UserDetail email={log.email} compact />
          </TableCell>
          <TableCell sx={{ color: defaultMessage ? "lightgray" : null }}>
            {log.userMessage}
          </TableCell>
          <TableCell>{log.description}</TableCell>
        </TableRow>
      );
    });
  }

  return (
    <EmptyRow>
      <BigLoader />
    </EmptyRow>
  );
};

const AuditLogs = () => {
  const { companyId } = useParams();

  const [startDate, setStartDate] = useState(() =>
    adjustDate(new Date(), { days: -14 })
  );
  const [endDate, setEndDate] = useState(null);
  const [email, setEmail] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const validDate = (d) => {
    if (!d || d.length < 10) return null;
    if (d.length > 10) d = d.substring(0, 10);
    if (/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(d)) return d;
    return null;
  };
  const validEmail = (e) => {
    if (!e) return null;
    return e;
  };
  const {
    data: auditLogs,
    status: auditLogsStatus,
    refetch: refetchAuditLogs,
  } = useListAuditLogsQuery({
    companyId: companyId || "ROOT",
    startDate: validDate(startDate),
    endDate: validDate(endDate),
    email: validEmail(email),
  });

  return (
    <AdminDashboard>
      <DashboardContent isFullWidth={true}>
        <Component>
          <Stack flexDirection={"row"} gap={1.5} alignItems={"center"}>
            <InlineSearch
              placeholder={"Search by email"}
              onChange={(searchText) => setEmail(searchText)}
            />
            <Box flexGrow={1} />
            <IconButton
              variant={"bright"}
              icon={AddIcon}
              onClick={() => setDialogOpen(true)}
            />
            <FormattedDatePicker
              label={"Start Date"}
              compact
              value={startDate}
              onChangeDate={setStartDate}
            />
            <FormattedDatePicker
              label={"End Date"}
              compact
              value={endDate}
              onChangeDate={setEndDate}
            />
            <Button onClick={refetchAuditLogs}>{"Refresh"}</Button>
          </Stack>
        </Component>

        <Component>
          <TableContainer sx={{ overflowX: "initial" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{"Date"}</TableCell>
                  <TableCell>{"User"}</TableCell>
                  <TableCell>{"Reason"}</TableCell>
                  <TableCell>{"Action"}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableContent status={auditLogsStatus} auditLogs={auditLogs} />
              </TableBody>
            </Table>
          </TableContainer>
        </Component>
      </DashboardContent>

      <ManualLogDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        companyId={companyId}
        onSuccess={refetchAuditLogs}
      />
    </AdminDashboard>
  );
};

export default AuditLogs;
